import { formatHandle, listenerLabel } from '../../lib/listenerName';
import type { Workbook, Worksheet } from 'exceljs';
import { END_REASON_LABEL, type ListenerRowStats } from './listenerStats';

/** `GET /admin/listeners/online-export` — day-wise online history per listener. */
export interface OnlineExport {
  range: { from: string; to: string; tz: string; now: string };
  /** Calendar days of the range in `range.tz`, oldest first (YYYY-MM-DD). */
  days: string[];
  /** True when the session list hit the server's row cap. */
  sessionsTruncated: boolean;
  listeners: Array<{
    id: string;
    displayName: string;
    handle: string;
    status: string;
    isSuspended: boolean;
    isDummy: boolean;
    /** Everything the Listeners table shows for this listener. Absent on older API builds. */
    details?: ListenerExportDetails;
    days: Array<{ day: string; onlineSeconds: number; screenOnSeconds: number }>;
    sessions: Array<{
      startedAt: string;
      endedAt: string | null;
      /** Clipped to the range — sums to the per-day online time. */
      durationSeconds: number;
      endReason: string | null;
    }>;
  }>;
}

export interface ListenerExportDetails {
  userId: string;
  phoneLast4: string;
  /** Full number — `null` unless the admin holds `users.reveal_phone`. */
  phone: string | null;
  gender: string | null;
  tier: string;
  languages: string[];
  ratingAvg: number;
  acceptsAudio: boolean;
  acceptsVideo: boolean;
  suspendedReason: string | null;
  rejectionReason: string | null;
  voiceVerifiedAt: string | null;
  createdAt: string;
  // ---- full profile (absent on older API builds) ----
  /** The account's own Display Name (may differ from the listener display name). */
  userDisplayName?: string | null;
  /** When the user account was created (before applying as a listener). */
  signedUpAt?: string;
  referralCode?: string | null;
  bio?: string;
  expertiseTags?: string[];
  acceptsChat?: boolean;
  /** 0–1 share of rings answered. */
  responseRate?: number;
  suspendedUntil?: string | null;
  /** The listener's own Online switch (on = wants calls). */
  onlineSwitch?: boolean;
  employment?: { status: string; type: string; employeeCode: string | null; joiningDate: string } | null;
  /** Latest document per type. */
  kyc?: Array<{ type: string; status: string; uploadedAt: string }>;
  payouts?: {
    /** Settled payouts, net, all time (paise). */
    paidPaise: string;
    /** Asked for, not yet settled (paise). */
    pendingPaise: string;
    accountType: string | null;
    holderName: string | null;
    accountLast4: string | null;
    /** From the latest payout request — `null` unless the admin holds `payouts.view`. */
    bank: {
      method: string | null;
      holderName: string | null;
      accountNumber: string | null;
      ifsc: string | null;
      upiId: string | null;
      asOf: string;
    } | null;
  };
  /** Figures for the export's date range; `login`/`online`/`money` are null without the permission. */
  stats: ListenerRowStats;
}

const RUPEE_FMT = '"₹"#,##0.00';

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  interview_scheduled: 'Interview scheduled',
  approved: 'Approved',
  rejected: 'Rejected',
};

/** Excel stores durations as a fraction of a day; `[h]` keeps totals past 24 h. */
const DURATION_FMT = '[h]:mm:ss';
const toExcelDuration = (seconds: number) => seconds / 86_400;

/** "2026-10-02" → "02 Oct 2026 (Fri)". */
export function dayLabel(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
  const weekday = d.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
  return `${date} (${weekday})`;
}

/** The calendar day (YYYY-MM-DD) of an instant in `tz`. */
function dayIn(iso: string, tz: string): string {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: tz });
}

/** "1:20:05 am" in `tz`. */
function timeIn(iso: string, tz: string): string {
  return new Date(iso).toLocaleTimeString('en-IN', {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** "02 Oct 2026, 1:20 am" in `tz`. */
function whenIn(iso: string, tz: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    timeZone: tz,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Excel sheet names: ≤ 31 chars, none of `[]:*?/\`, unique (case-insensitive),
 * and not reserved by another sheet. Falls back to the handle for a blank name.
 */
export function sheetName(name: string, handle: string, taken: Set<string>): string {
  const clean = (s: string) => s.replace(/[[\]:*?/\\]/g, ' ').replace(/\s+/g, ' ').trim().replace(/^'+|'+$/g, '');
  const base = clean(name) || clean(handle) || 'Listener';
  let candidate = base.slice(0, 31);
  for (let n = 2; taken.has(candidate.toLowerCase()); n++) {
    const suffix = ` (${n})`;
    candidate = base.slice(0, 31 - suffix.length) + suffix;
  }
  taken.add(candidate.toLowerCase());
  return candidate;
}

function styleHeader(ws: Worksheet, rowNumber: number) {
  const row = ws.getRow(rowNumber);
  row.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  row.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD6336C' } };
    cell.alignment = { vertical: 'middle', wrapText: true };
  });
}

const DETAILS_SHEET = 'Listener details';

const KYC_TYPE_LABEL: Record<string, string> = { pan: 'PAN', aadhaar: 'Aadhaar', licence: 'Licence', other: 'Other' };

/** "SBI · A Kumar · ••1234" — the payout account as far as this admin may see it. */
function payoutAccountText(p: ListenerExportDetails['payouts']): string {
  if (!p) return '';
  const holder = p.bank?.holderName ?? p.holderName;
  const kind = p.bank?.method ?? p.accountType;
  const tail = p.accountLast4 ? `••${p.accountLast4}` : '';
  return [kind, holder, tail].filter(Boolean).join(' · ');
}

/**
 * The "Listener details" sheet: one row per listener with everything the
 * Listeners table shows — phone, login and online state, and the range's login /
 * online / call time — plus calls and earnings. A figure the admin may not see
 * (no activity / earnings permission) is left blank, never written as zero.
 */
function addDetailsSheet(wb: Workbook, data: OnlineExport, rangeText: string) {
  const tz = data.range.tz;
  const ws = wb.addWorksheet(DETAILS_SHEET, { views: [{ state: 'frozen', xSplit: 1, ySplit: 3 }] });
  ws.getCell('A1').value = 'Listener details';
  ws.getCell('A1').font = { bold: true, size: 14 };
  ws.getCell('A2').value = `${rangeText} · time and money columns cover this range · as of ${whenIn(data.range.now, tz)}`;

  const columns: Array<{ header: string; width: number; numFmt?: string }> = [
    { header: 'Listener', width: 26 },
    { header: 'Handle', width: 20 },
    { header: 'Phone', width: 18 },
    { header: 'Status', width: 20 },
    { header: 'Tier', width: 10 },
    { header: 'Gender', width: 12 },
    { header: 'Languages', width: 16 },
    { header: 'Rating', width: 9, numFmt: '0.0' },
    { header: 'Takes audio calls', width: 12 },
    { header: 'Takes video calls', width: 12 },
    { header: 'Voice verified', width: 22 },
    { header: 'Joined', width: 22 },
    { header: 'Account name', width: 22 },
    { header: 'Signed up', width: 22 },
    { header: 'Bio', width: 40 },
    { header: 'Expertise', width: 28 },
    { header: 'Takes chat', width: 10 },
    { header: 'Response rate', width: 11, numFmt: '0%' },
    { header: 'Online switch', width: 11 },
    { header: 'Suspended until', width: 22 },
    { header: 'Employee', width: 24 },
    { header: 'KYC', width: 34 },
    { header: 'Referral code', width: 14 },
    { header: 'Paid out (all time)', width: 15, numFmt: RUPEE_FMT },
    { header: 'Payout pending', width: 15, numFmt: RUPEE_FMT },
    { header: 'Payout account', width: 24 },
    { header: 'Bank account no.', width: 20 },
    { header: 'IFSC', width: 13 },
    { header: 'UPI ID', width: 24 },
    { header: 'Login status', width: 13 },
    { header: 'Last login', width: 22 },
    { header: 'Last logout', width: 22 },
    { header: 'Login time', width: 13, numFmt: DURATION_FMT },
    { header: 'Online status', width: 14 },
    { header: 'Online since', width: 22 },
    { header: 'Last online', width: 22 },
    { header: 'Online time', width: 13, numFmt: DURATION_FMT },
    { header: 'Calls', width: 9 },
    { header: 'Connected calls', width: 11 },
    { header: 'Call time', width: 13, numFmt: DURATION_FMT },
    { header: 'Coins spent by callers', width: 14 },
    { header: 'Call earnings', width: 15, numFmt: RUPEE_FMT },
    { header: 'Total earnings', width: 15, numFmt: RUPEE_FMT },
    { header: 'Suspended / rejected reason', width: 36 },
    { header: 'Listener ID', width: 38 },
    { header: 'User ID', width: 38 },
  ];
  ws.getRow(3).values = columns.map((c) => c.header);
  styleHeader(ws, 3);
  columns.forEach((c, i) => {
    const col = ws.getColumn(i + 1);
    col.width = c.width;
    if (c.numFmt) col.numFmt = c.numFmt;
  });

  const when = (iso: string | null | undefined) => (iso ? whenIn(iso, tz) : '');
  const yesNo = (v: boolean) => (v ? 'Yes' : 'No');
  for (const l of data.listeners) {
    const d = l.details;
    if (!d) continue;
    const { login, online, calls, money } = d.stats;
    ws.addRow([
      listenerLabel({ displayName: l.displayName, handle: l.handle }),
      formatHandle(l.handle) ?? '',
      // Text, so Excel keeps the leading "+" and every digit.
      d.phone ?? `••• ${d.phoneLast4}`,
      [STATUS_LABEL[l.status] ?? l.status, l.isSuspended ? 'Suspended' : '', l.isDummy ? 'Dummy' : '']
        .filter(Boolean)
        .join(' · '),
      d.tier,
      d.gender ?? '',
      d.languages.join(', '),
      d.ratingAvg > 0 ? d.ratingAvg : null,
      yesNo(d.acceptsAudio),
      yesNo(d.acceptsVideo),
      when(d.voiceVerifiedAt),
      when(d.createdAt),
      d.userDisplayName ?? '',
      when(d.signedUpAt),
      d.bio ?? '',
      (d.expertiseTags ?? []).join(', '),
      d.acceptsChat === undefined ? '' : yesNo(d.acceptsChat),
      d.responseRate ?? null,
      d.onlineSwitch === undefined ? '' : d.onlineSwitch ? 'On' : 'Off',
      when(d.suspendedUntil),
      d.employment
        ? [d.employment.employeeCode, d.employment.type, d.employment.status].filter(Boolean).join(' · ')
        : '',
      (d.kyc ?? []).map((k) => `${KYC_TYPE_LABEL[k.type] ?? k.type}: ${k.status}`).join(', '),
      d.referralCode ?? '',
      d.payouts ? Number(d.payouts.paidPaise) / 100 : null,
      d.payouts ? Number(d.payouts.pendingPaise) / 100 : null,
      payoutAccountText(d.payouts),
      // Text, so Excel keeps leading zeros.
      d.payouts?.bank?.accountNumber ?? '',
      d.payouts?.bank?.ifsc ?? '',
      d.payouts?.bank?.upiId ?? '',
      !login ? '' : login.loggedIn === null ? 'No data' : login.loggedIn ? 'Logged in' : 'Logged out',
      when(login?.lastLoginAt),
      when(login?.lastLogoutAt),
      login ? toExcelDuration(login.loginSeconds) : null,
      !online ? '' : online.onlineNow ? 'Online' : 'Offline',
      when(online?.currentOnlineSince),
      when(online?.lastOnlineAt),
      online ? toExcelDuration(online.onlineSeconds) : null,
      calls.calls,
      calls.connectedCalls,
      toExcelDuration(calls.callSeconds),
      money ? money.callerCoins : null,
      money ? Number(money.callEarningsPaise) / 100 : null,
      money ? Number(money.earningsPaise) / 100 : null,
      d.suspendedReason ?? d.rejectionReason ?? '',
      l.id,
      d.userId,
    ]);
  }
}

/**
 * Builds the workbook: a "Listener details" sheet (when the API sends them),
 * a "Summary" sheet (one row per listener, one column per
 * day of online time) followed by one sheet per listener with the day-wise
 * online / screen-on time and every online session in the range.
 */
export async function buildOnlineWorkbook(data: OnlineExport): Promise<Workbook> {
  // exceljs is large — loaded only when someone exports.
  const mod = await import('exceljs');
  const ExcelJS = mod.default ?? mod;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Dude admin';
  wb.created = new Date();
  const tz = data.range.tz;
  const lastDay = dayIn(new Date(Date.parse(data.range.to) - 1).toISOString(), tz);
  const rangeText = `${dayLabel(data.days[0] ?? dayIn(data.range.from, tz))} – ${dayLabel(data.days.at(-1) ?? lastDay)} · ${tz}`;
  const taken = new Set<string>(['summary', DETAILS_SHEET.toLowerCase()]);
  if (data.listeners.some((l) => l.details)) addDetailsSheet(wb, data, rangeText);

  // ---- Summary -------------------------------------------------------------
  const summary = wb.addWorksheet('Summary', { views: [{ state: 'frozen', xSplit: 1, ySplit: 3 }] });
  summary.getCell('A1').value = 'Listener online time per day';
  summary.getCell('A1').font = { bold: true, size: 14 };
  summary.getCell('A2').value = rangeText;
  summary.getRow(3).values = [
    'Listener',
    'Handle',
    'Status',
    ...data.days.map(dayLabel),
    'Total online',
    'Total screen-on',
    'Sheet',
  ];
  styleHeader(summary, 3);
  summary.getColumn(1).width = 26;
  summary.getColumn(2).width = 20;
  summary.getColumn(3).width = 18;
  data.days.forEach((_, i) => {
    const col = summary.getColumn(4 + i);
    col.width = 13;
    col.numFmt = DURATION_FMT;
  });
  for (const offset of [0, 1]) {
    const col = summary.getColumn(4 + data.days.length + offset);
    col.width = 15;
    col.numFmt = DURATION_FMT;
  }
  summary.getColumn(6 + data.days.length).width = 22;

  for (const l of data.listeners) {
    const name = sheetName(l.displayName, l.handle, taken);
    const totalOnline = l.days.reduce((t, d) => t + d.onlineSeconds, 0);
    const totalScreen = l.days.reduce((t, d) => t + d.screenOnSeconds, 0);
    const status = [STATUS_LABEL[l.status] ?? l.status, l.isSuspended ? 'Suspended' : '', l.isDummy ? 'Dummy' : '']
      .filter(Boolean)
      .join(' · ');
    const row = summary.addRow([
      listenerLabel({ displayName: l.displayName, handle: l.handle }),
      formatHandle(l.handle) ?? '',
      status,
      ...l.days.map((d) => toExcelDuration(d.onlineSeconds)),
      toExcelDuration(totalOnline),
      toExcelDuration(totalScreen),
      name,
    ]);
    row.getCell(6 + data.days.length).value = {
      text: name,
      hyperlink: `#'${name.replace(/'/g, "''")}'!A1`,
    };

    // ---- One sheet per listener ---------------------------------------------
    const ws = wb.addWorksheet(name);
    ws.getCell('A1').value = listenerLabel({ displayName: l.displayName, handle: l.handle });
    ws.getCell('A1').font = { bold: true, size: 14 };
    ws.getCell('A2').value = [formatHandle(l.handle), l.details?.phone, status].filter(Boolean).join(' · ');
    ws.getCell('A3').value = rangeText;
    ws.getCell('A4').value = { text: '← Summary', hyperlink: "#'Summary'!A1" };

    const byDay = new Map<string, OnlineExport['listeners'][number]['sessions']>();
    for (const s of l.sessions) {
      const day = dayIn(s.startedAt, tz);
      byDay.set(day, [...(byDay.get(day) ?? []), s]);
    }

    ws.getRow(6).values = ['Date', 'Online time', 'Screen-on time', 'Sessions', 'First online', 'Last offline'];
    styleHeader(ws, 6);
    for (const d of l.days) {
      const daySessions = byDay.get(d.day) ?? [];
      const last = daySessions.at(-1);
      ws.addRow([
        dayLabel(d.day),
        toExcelDuration(d.onlineSeconds),
        toExcelDuration(d.screenOnSeconds),
        daySessions.length,
        daySessions[0] ? timeIn(daySessions[0].startedAt, tz) : '—',
        !last ? '—' : last.endedAt ? timeIn(last.endedAt, tz) : 'Still online',
      ]);
    }
    const totalRow = ws.addRow(['Total', toExcelDuration(totalOnline), toExcelDuration(totalScreen), l.sessions.length]);
    totalRow.font = { bold: true };

    ws.addRow([]);
    const titleRow = ws.addRow(['Online sessions']);
    titleRow.font = { bold: true, size: 12 };
    const headerRow = ws.addRow(['#', 'Date', 'Went online', 'Went offline', 'Duration', 'How it ended']);
    styleHeader(ws, headerRow.number);
    if (l.sessions.length === 0) ws.addRow(['', 'No online sessions in this range']);
    l.sessions.forEach((s, i) => {
      ws.addRow([
        i + 1,
        dayLabel(dayIn(s.startedAt, tz)),
        whenIn(s.startedAt, tz),
        s.endedAt ? whenIn(s.endedAt, tz) : 'Still online',
        toExcelDuration(s.durationSeconds),
        s.endReason ? (END_REASON_LABEL[s.endReason] ?? s.endReason) : s.endedAt ? '—' : '',
      ]);
    });

    ws.getColumn(1).width = 20;
    ws.getColumn(2).width = 18;
    ws.getColumn(3).width = 22;
    ws.getColumn(4).width = 22;
    ws.getColumn(5).width = 14;
    ws.getColumn(6).width = 24;
    // Durations: column B/C in the day table, column E in the session list.
    for (let r = 7; r <= totalRow.number; r++) {
      ws.getCell(r, 2).numFmt = DURATION_FMT;
      ws.getCell(r, 3).numFmt = DURATION_FMT;
    }
    for (let r = headerRow.number + 1; r <= ws.rowCount; r++) ws.getCell(r, 5).numFmt = DURATION_FMT;
  }

  if (data.sessionsTruncated) {
    summary.getCell('A2').value = `${rangeText} · session lists truncated — pick a shorter range`;
  }
  return wb;
}

/** Builds the workbook and hands it to the browser as an .xlsx download. */
export async function downloadOnlineWorkbook(data: OnlineExport, fileName: string): Promise<void> {
  const wb = await buildOnlineWorkbook(data);
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
